import type { CommentDto } from "../api/types";

/** Sort orders offered in the comments UI. */
export type CommentSort = "best" | "new" | "old" | "top";

/** A comment plus its nested replies. */
export interface CommentNode {
  comment: CommentDto;
  children: CommentNode[];
  /** Number of replies at any depth beneath this node. */
  descendantCount: number;
}

/** Comparator for a given sort order, applied at every level of the tree. */
function comparator(sort: CommentSort): (a: CommentDto, b: CommentDto) => number {
  const byNewest = (a: CommentDto, b: CommentDto) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  switch (sort) {
    case "new":
      return byNewest;
    case "old":
      return (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    case "top":
      return (a, b) => b.upvotes - a.upvotes || b.score - a.score || byNewest(a, b);
    case "best":
    default:
      return (a, b) => b.score - a.score || byNewest(a, b);
  }
}

/**
 * Assemble a flat comment list into a reply tree, ordering siblings at every
 * level by the chosen sort. Replies whose parent is absent from the list (e.g.
 * filtered out) are promoted to top level so nothing is silently dropped.
 */
export function buildCommentTree(
  flat: CommentDto[],
  sort: CommentSort = "best",
): CommentNode[] {
  const nodes = new Map<number, CommentNode>();
  for (const c of flat) {
    nodes.set(c.id, { comment: c, children: [], descendantCount: 0 });
  }

  const roots: CommentNode[] = [];
  for (const c of flat) {
    const node = nodes.get(c.id)!;
    const parent = c.parentId != null ? nodes.get(c.parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const cmp = comparator(sort);
  const sortLevel = (level: CommentNode[]): number => {
    level.sort((a, b) => cmp(a.comment, b.comment));
    for (const n of level) {
      const childCount = sortLevel(n.children);
      n.descendantCount = n.children.length + childCount;
    }
    return level.reduce((sum, n) => sum + 1 + n.descendantCount, 0) - level.length;
  };
  sortLevel(roots);

  return roots;
}

/** Total number of comments represented by a list of nodes (nodes + descendants). */
export function countNodes(nodes: CommentNode[]): number {
  return nodes.reduce((sum, n) => sum + 1 + n.descendantCount, 0);
}
